import React, { useEffect, useState } from "react";
import { marketingApi } from "../marketingApi";
import { Section, EmptyState, Loading, StatusDot } from "../components";
import { Button } from "@/components/ui/button";
import { ExternalLink, Lock, ShieldCheck } from "lucide-react";
import { WRITE_CAPABLE_SLUGS, channelActionLabel, integrationsBanner } from "../writeState";

export default function IntegrationsTab() {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState(null); // runtime status from Director IA backend
  const [err, setErr] = useState(null);

  useEffect(() => {
    Promise.all([
      marketingApi.integrations().catch((e) => { throw e; }),
      marketingApi.director.status().catch(() => ({ writes_enabled: false })),
    ])
      .then(([d, s]) => { setData(d); setStatus(s); })
      .catch((e) => setErr(e?.response?.data?.detail || "Error"));
  }, []);

  if (err) return <EmptyState title="Error al cargar" description={err} testId="integrations-error" />;
  if (!data || !status) return <Loading />;

  const writesEnabled = !!status.writes_enabled;
  const adapters = data.adapters || [];

  return (
    <div className="space-y-4" data-testid="integrations-tab">
      {writesEnabled ? (
        <div
          className="flex items-start gap-3 p-4 bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-900 rounded-lg"
          data-testid="integrations-writes-enabled-banner"
          role="status"
        >
          <ShieldCheck size={16} className="text-emerald-700 dark:text-emerald-400 mt-0.5" />
          <div className="text-sm text-emerald-900 dark:text-emerald-200">
            <b>Capacidad operativa activada.</b> Toda acción externa requiere revisión y
            aprobación explícita antes de ejecutarse. Las aprobaciones usan nonce de un solo
            uso, idempotencia y auditoría sanitizada.
          </div>
        </div>
      ) : (
        <div
          className="flex items-start gap-3 p-4 bg-sky-50 dark:bg-sky-950/30 border border-sky-100 dark:border-sky-900 rounded-lg"
          data-testid="integrations-writes-disabled-banner"
          role="status"
        >
          <Lock size={16} className="text-sky-700 dark:text-sky-400 mt-0.5" />
          <div className="text-sm text-sky-900 dark:text-sky-200">
            <b>Modo lectura obligatorio.</b> Las escrituras externas están bloqueadas globalmente
            (<code className="text-[11px] bg-sky-100 dark:bg-sky-900 px-1 rounded">MARKETING_WRITE_ENABLED=0</code>).
            Ningún canal puede publicar posts, enviar emails ni modificar presupuestos desde aquí.
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {adapters.map((a) => {
          const canWrite = writesEnabled && WRITE_CAPABLE_SLUGS.has(a.slug);
          return (
            <Section
              key={a.slug}
              testId={`integration-${a.slug}`}
              title={a.label}
              subtitle={a.description}
              right={<StatusDot ok={a.connected} label={a.connected ? "Conectado" : "No conectado"} />}
            >
              <div className="space-y-3">
                <div className="text-[11px] text-slate-500">
                  Campos requeridos: {a.required_fields.map((f) => (
                    <code key={f} className={`text-[10px] px-1.5 py-0.5 rounded mr-1 ${a.missing_fields.includes(f) ? "bg-amber-50 text-amber-800 border border-amber-200" : "bg-emerald-50 text-emerald-800 border border-emerald-200"}`}>{f}</code>
                  ))}
                </div>
                {a.missing_fields.length > 0 && (
                  <div className="text-xs text-slate-600 dark:text-slate-300">
                    Faltan {a.missing_fields.length} credenciales. Añádelas desde el gestor de Secretos de Emergent y vuelve a cargar.
                  </div>
                )}
                <div className="flex items-center justify-between pt-1">
                  <a href={a.docs_url} target="_blank" rel="noopener noreferrer" className="text-xs text-[#026FCB] hover:underline inline-flex items-center gap-1">
                    Documentación <ExternalLink size={11} />
                  </a>
                  {canWrite ? (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled
                      className="text-xs border-emerald-300 text-emerald-800 bg-emerald-50 hover:bg-emerald-50 disabled:opacity-100"
                      title="Las acciones se preparan desde el Director IA y requieren aprobación explícita"
                      data-testid={`integration-${a.slug}-action-btn`}
                    >
                      <ShieldCheck size={11} className="mr-1" /> Acciones con aprobación
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled
                      className="text-xs"
                      title={writesEnabled ? "Canal sólo lectura (sin executor aprobado)" : "Escrituras bloqueadas en esta fase"}
                      data-testid={`integration-${a.slug}-action-btn`}
                    >
                      <Lock size={11} className="mr-1" />
                      {writesEnabled ? "Sólo lectura" : "Escrituras bloqueadas"}
                    </Button>
                  )}
                </div>
              </div>
            </Section>
          );
        })}
      </div>
    </div>
  );
}
